import {
  SystemCssProperties,
  SystemStyleObject,
} from '@mui/system/styleFunctionSx/styleFunctionSx';
import { ReactNode, useMemo } from 'react';
import { Box, Grid, Stack, SxProps } from '@mui/material';

import { getCalendarGridStyle } from './utils';

import { CALENDAR_GRID_DEFAULTS } from '../../constants';
import { getCalendarGridColors, normaliseSx } from '../../utils';
import { CalendarGridBaseProps } from '../../types';

export type CalendarGridColumnProps = CalendarGridBaseProps & {
  cellCount: number;
  cellColor?: string;
  renderHeader?: null | ((textColor: string) => ReactNode);
  renderCellContent?: null | ((index: number, id: string, textColor: string) => ReactNode);
  tinted?: boolean;
  sx?: SxProps;
  children?: ReactNode | ReactNode[];
};

export const CalendarGridColumn = ({
  sx,
  cellCount,
  renderHeader,
  renderCellContent,
  minWidth,
  cellColor = CALENDAR_GRID_DEFAULTS.color,
  gridColor = CALENDAR_GRID_DEFAULTS.gridColor,
  gridStep = CALENDAR_GRID_DEFAULTS.gridStep,
  gridWidth = CALENDAR_GRID_DEFAULTS.gridWidth,
  borderTop,
  borderRight,
  borderBottom,
  borderLeft,
  tinted,
  children,
}: CalendarGridColumnProps) => {
  const gridColors = useMemo(() => getCalendarGridColors(gridColor), [gridColor]);

  const columnStyle = useMemo(
    () => ({
      ...defaultColumnStyle,
      ...getCalendarGridStyle(gridColors.gridColor, {
        top: borderTop,
        bottom: borderBottom,
        left: borderLeft,
        right: borderRight,
      }),
      minWidth: minWidth || 'auto',
    }),
    [gridColors.gridColor, borderTop, borderBottom, borderLeft, borderRight, minWidth],
  );

  const currentCellColor = tinted ? gridColors.tintColor : cellColor;

  const cellStyle = useMemo(
    () => ({
      height: gridStep,
      backgroundColor: currentCellColor,
      borderBottomWidth: gridWidth,
      borderBottomColor: gridColors.gridColor,
    }),
    [currentCellColor, gridColors.gridColor, gridStep, gridWidth],
  );

  const cells = useMemo(
    () => Array.from({ length: cellCount }, (_, i) => `calendar-grid-cell-${i}`),
    [cellCount],
  );

  return (
    <Stack sx={[columnStyle, ...normaliseSx(sx)]}>
      {!!renderHeader && (
        <Box sx={[defaultHeaderStyle, cellStyle]}>{renderHeader(gridColors.textColor)}</Box>
      )}
      <Grid container direction='column' wrap='nowrap' sx={defaultGridContainerStyle}>
        {cells.map((id, index) => (
          <Grid item id={id} key={id} sx={[defaultCellStyle, cellStyle]}>
            {renderCellContent?.(index, id, gridColors.textColor)}
          </Grid>
        ))}
        {!!children && <Stack sx={defaultContentContainerStyle}>{children}</Stack>}
      </Grid>
    </Stack>
  );
};

const defaultColumnStyle: SystemCssProperties = {
  width: '100%',
};

const defaultCellStyle: SystemCssProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  borderBottomStyle: 'solid',
  userSelect: 'none',
};

const defaultHeaderStyle: SystemCssProperties = {
  ...defaultCellStyle,
  justifyContent: 'flex-start',
};

const defaultGridContainerStyle: SystemStyleObject = {
  padding: 0,
  margin: 0,
  paddingLeft: '0!important',
  paddingRight: '0!important',
  position: 'relative',
  '& > .MuiGrid-item:last-child': {
    borderBottomStyle: 'none',
  },
};

const defaultContentContainerStyle: SystemCssProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
};
